import * as React from 'react';
import * as Hammer from 'hammerjs';

import './styles.css';
import { IProject, Map } from '../../../@types/models/Project';
import { ScreenProvider, ScreenContextProps } from '../../_context';
import { Project } from '../Project';

export interface RailProps {
    maps: Map[];
    getProject: (uid: string) => Promise<IProject>;
    width: number;
    height: number;

    showCaption: boolean;
    isCaptionHovered: boolean;

    closeCaption: () => void;
}

interface State {
    width: number;
    height: number;
    maps: Map[];
    current: number;
    drag: number;
    previous: number;
}

class WrappedRail extends React.Component<RailProps & ScreenContextProps, State> {
    private hammer: HammerManager;

    private container: HTMLDivElement | null;
    static ScalarDistance = (ev: HammerInput) => {
        switch (ev.offsetDirection) {
            case Hammer.DIRECTION_RIGHT:
                return -ev.distance;
            case Hammer.DIRECTION_LEFT:
                return ev.distance;
            default:
                return null;
        }

    }

    static ApplyMap(width: number, height: number, maps: Map[]): Map[] {
        var result = [];
        for (let i = 0; i < maps.length; i++) {
            const m = maps[i];

            const prev: (Map | undefined) = i !== 0 && result[i - 1] || undefined;
            const prevX: number = prev && prev.x || 0;
            const prevW: number = prev && prev.width || 0;

            const ratio = m.imageWidth / m.imageHeight;
            let h = Math.min(height, 1070);

            h = ratio > (3 / 4) ? (h - 70) : h;

            const x = prevX + prevW;
            const w = Math.floor(Math.min(ratio * h, width, 1500, m.imageWidth) + 10);
            const p = -x - w / 2;

            result.push({
                ...m,
                index: i,
                width: w,
                x: x,
                position: p
            });
        }
        return result;
    }

    static getDerivedStateFromProps(nextProps: RailProps & ScreenContextProps, prevState: State): State {
        const { width, height, maps } = nextProps;
        if (prevState.height !== height || prevState.width !== width || prevState.maps.length !== maps.length) {
            const newMaps = WrappedRail.ApplyMap(width, height, maps);
            return { ...prevState, width: width, height: height, maps: newMaps };
        }
        return prevState;
    }

    public constructor(props: RailProps & ScreenContextProps) {
        super(props);

        this._handleClick = this._handleClick.bind(this);
        this._handleKeydown = this._handleKeydown.bind(this);
        const { width, height, maps } = props;
        this.state = {
            maps: WrappedRail.ApplyMap(width, height, maps),
            width: this.props.width,
            height: this.props.height,
            current: 0,
            previous: 0,
            drag: 0
        };
    }

    componentDidMount() {
        window.addEventListener('keydown', this._handleKeydown);
        if (this.container) {
            this.hammer = new Hammer(this.container);
            this.hammer.get('pan').set({ direction: Hammer.DIRECTION_HORIZONTAL });
            this.hammer.on('panstart', this._OnDragStart.bind(this));
            this.hammer.on('panmove', this._OnDrag.bind(this));
            this.hammer.on('panend', this._OnDragEnd.bind(this));
        }
    }
    componentWillUnmount() {
        window.removeEventListener('keydown', this._handleKeydown);
        if (this.hammer) {
            this.hammer.destroy();
        }
    }

    public render() {
        const cls = ['rail'];
        const { current, drag, previous } = this.state;

        if (current > previous) {
            cls.push('rail--move-right');
        } else {
            cls.push('rail--move-left');
        }

        if (this.props.screenRatio === 'landscape') {
            cls.push('rail--landscape');
        } else {
            cls.push('rail--portrait');
        }

        if (drag !== 0) {
            cls.push('rail--dragging');
        }

        const translate = this._getMap(current);
        const torender = this._toRender();

        const translate3d = `translate3d(${translate.position || 0}px,0,0)`;

        const transform = `${translate3d} translateX(${-drag}px)`;

        return (
            <div ref={(c) => { this.container = c; }} className={cls.join(' ')} >
                <a
                    // tslint:disable-next-line:max-line-length
                    className={`rail__nav-btn rail__nav-btn--previous ${this.props.showCaption ? 'rail__nav-btn--hide' : ''}`}
                    onClick={(e) => {
                        e.preventDefault();
                        this._handleClick(current - 1);
                    }}
                />
                <div className="rail__container">
                    <div
                        className="rail__container-translate "
                        style={{ transform }}
                    >
                        {torender.map((m) => {
                            const i = m.index || 0;
                            const clsItem = ['rail__item'];
                            if (current === i) {
                                clsItem.push('rail__item--current');
                                if (this.props.showCaption) {
                                    clsItem.push('rail__item--captions');
                                }
                                if (this.props.isCaptionHovered) {
                                    clsItem.push('rail__item--captions-hovered');
                                }
                            } else if (current > i) {
                                clsItem.push('rail__item--before');
                            } else {
                                clsItem.push('rail__item--after');
                            }

                            return (
                                <div
                                    key={i}
                                    className={clsItem.join(' ')}
                                    style={{ left: m.x, width: m.width || 0 }}
                                >
                                    <Project
                                        map={m}
                                        getProject={this.props.getProject}
                                    />
                                </div>);
                        })}
                    </div>
                </div>
                <a
                    // tslint:disable-next-line:max-line-length
                    className={`rail__nav-btn rail__nav-btn--next ${this.props.showCaption ? 'rail__nav-btn--hide' : ''}`}
                    onClick={(e) => {
                        e.preventDefault();
                        this._handleClick(current + 1);
                    }}
                />
            </div>
        );
    }

    private _handleKeydown(e: KeyboardEvent) {
        switch (e.keyCode) {
            case 37: // left
                this._Previous();
                break;
            case 38: // up
                break;
            case 39: // right
                this._Next();
                break;
            case 40: // down
                break;
            default:
                break;
        }
    }

    private _OnDragStart(ev: HammerInput) {

        // add dragging to state ?
        this._OnDrag(ev);
        return false;
    }

    private _OnDrag(ev: HammerInput) {

        const distance = WrappedRail.ScalarDistance(ev);
        if (!distance) { return false; }
        this.setState(prev => ({ drag: distance }));
        return false;
    }

    private _OnDragEnd(ev: HammerInput) {
        this._OnDrag(ev);
        const distance = WrappedRail.ScalarDistance(ev);
        const container = this.container;
        if (!distance) { return; }
        if (!container) { return; }

        const direction = distance / Math.abs(distance);
        const percent = Math.abs(distance / container.clientWidth);

        if (percent < 0.3) {
            this.setState(prev => ({ drag: 0 }));
            return false;
        }

        const { current } = this.state;
        this.setState(prev => ({ drag: 0 }));
        this._Go(current + Math.ceil(percent) * direction);
        this.props.closeCaption();
        return false;
    }

    private _Go(i: number) {
        this.setState(prev => ({
            current: i,
            previous: prev.current
        }));
    }

    private _Next() {
        this._Go(this.state.current + 1);
    }

    private _Previous() {
        this._Go(this.state.current - 1);
    }

    private _handleClick(i: number) {
        this._Go(i);
    }

    private _getMap(i: number): Map {
        const count = this.state.maps.length;

        const last = this.state.maps[count - 1];
        const W = (last.x || 0) + (last.width || 0);
        const n = Math.floor(i / count);
        const index = (i + Math.abs(n) * count) % count;
        const projected = this.state.maps[index];

        return {
            ...projected,
            x: (projected.x || 0) + n * W,
            index: i,
            position: (projected.position || 0) - n * W,
        };
    }

    private _getMinMax(i: number) {

        const current = this._getMap(i);
        let minIndex = i - 1;
        let maxIndex = i + 1;

        const available = this.props.width / 2 - (current.width || 0) / 2;

        let availableRight = available;
        while (availableRight > 0) {

            const next = this._getMap(maxIndex);
            const nextWidth = next.width;

            if (!nextWidth) {
                break;
            }
            availableRight -= nextWidth;
            maxIndex += 1;
        }

        let availableLeft = available;
        while (availableLeft > 0) {

            const prev = this._getMap(minIndex);
            const prevWidth = prev.width;
            if (!prevWidth) {
                break;
            }
            availableLeft -= prevWidth;
            minIndex -= 1;
        }
        return { min: minIndex, max: maxIndex };
    }

    private _toRender(): Map[] {
        var result: Map[] = [];

        const { min: minPrevious, max: maxPrevious } = this._getMinMax(this.state.previous);
        const { min: minCurrent, max: maxCurrent } = this._getMinMax(this.state.current);

        const min = Math.min(minPrevious, minCurrent) - 1;
        const max = Math.max(maxPrevious, maxCurrent) + 1;

        for (var i = min; i <= max; i++) {
            result.push(this._getMap(i));
        }

        return result;
    }
}
export const Rail = ScreenProvider.Inject(WrappedRail);
export default Rail;


// WEBPACK FOOTER //
// ./src/components/Rail/Component.tsx